import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Timer, ChevronRight, Play } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { formatTimeMMSS } from '../utils/formatTime';

interface ContractionData {
  hasActiveContraction: boolean;
  activeStartTime: string | null;
  lastEndTime: string | null;
  count: number;
}

export function ActiveContractionCard() {
  const { currentPregnancy } = usePregnancyStore();
  const [data, setData] = useState<ContractionData | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timeSinceLast, setTimeSinceLast] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch contraction data
  useEffect(() => {
    if (!currentPregnancy?.id) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        // Check for active contraction (end_time is null)
        const { data: activeContraction } = await supabase
          .from('contractions')
          .select('id, start_time')
          .eq('pregnancy_id', currentPregnancy.id)
          .is('end_time', null)
          .order('start_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        // Get count of today's contractions
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const { count } = await supabase
          .from('contractions')
          .select('*', { count: 'exact', head: true })
          .eq('pregnancy_id', currentPregnancy.id)
          .gte('start_time', today.toISOString());

        // Get last completed contraction for "time since"
        const { data: lastContraction } = await supabase
          .from('contractions')
          .select('id, end_time')
          .eq('pregnancy_id', currentPregnancy.id)
          .not('end_time', 'is', null)
          .order('end_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (activeContraction || (count && count > 0)) {
          const newData: ContractionData = {
            hasActiveContraction: !!activeContraction,
            activeStartTime: activeContraction?.start_time || null,
            lastEndTime: lastContraction?.end_time || null,
            count: count || 0,
          };
          setData(newData);

          // Calculate time since last
          if (lastContraction?.end_time) {
            const endTime = new Date(lastContraction.end_time).getTime();
            const secondsSince = Math.floor((Date.now() - endTime) / 1000);
            setTimeSinceLast(secondsSince);
          }
        } else {
          setData(null);
        }
      } catch (error) {
        console.error('Error fetching contraction data:', error);
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('home-contractions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contractions',
          filter: `pregnancy_id=eq.${currentPregnancy.id}`,
        },
        () => {
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentPregnancy?.id]);

  // Timer for active contraction
  useEffect(() => {
    if (!data?.hasActiveContraction || !data.activeStartTime) {
      setElapsedSeconds(0);
      return;
    }

    const startTime = new Date(data.activeStartTime).getTime();
    setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));

    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [data?.hasActiveContraction, data?.activeStartTime]);

  // Timer for time since last contraction
  useEffect(() => {
    if (!data || data.hasActiveContraction || !data.lastEndTime) return;

    const interval = setInterval(() => {
      setTimeSinceLast((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [data?.hasActiveContraction, data?.lastEndTime]);

  // Don't show if loading or no data
  if (isLoading || !data) {
    return null;
  }

  const isActive = data.hasActiveContraction;

  return (
    <Link to="/tools/contractions" className="block">
      <div className={isActive ? 'bg-pp-orange rounded-[15px] p-4' : 'glass-effect rounded-[15px] p-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-pp-orange'}`}>
              {isActive ? (
                <Timer className="w-6 h-6 text-white" />
              ) : (
                <Play className="w-6 h-6 text-white" fill="currentColor" />
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold">
                {isActive ? 'Wehe läuft' : 'Seit letzter Wehe'}
              </h3>
              <p className="text-white/80 text-sm">
                {isActive
                  ? `${formatTimeMMSS(elapsedSeconds)} • ${data.count} Wehen heute`
                  : `${formatTimeMMSS(timeSinceLast)} • ${data.count} Wehen heute`}
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-white/80" />
        </div>
      </div>
    </Link>
  );
}
